import { useEffect, useRef, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FolderLock, UploadSimple, Trash, Lock, DownloadSimple, FilePlus, ClockCounterClockwise, ShareNetwork, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = ["NDA", "LOI", "Bilan", "Compte de résultat", "Portefeuille", "Contrat salarié", "Convention", "Autre"];
const TEAM_ROLES = ["super_admin", "acquereur", "analyste", "associe"];
const LEVELS = [
  { value: "lecture", label: "Lecture seule" },
  { value: "telechargement", label: "Lecture + téléchargement" },
  { value: "gestion", label: "Gestion complète" },
];
const LEVEL_LABEL = Object.fromEntries(LEVELS.map((l) => [l.value, l.label]));

const fmtSize = (b) => {
  if (!b && b !== 0) return "—";
  if (b >= 1048576) return (b / 1048576).toFixed(1) + " Mo";
  if (b >= 1024) return Math.round(b / 1024) + " Ko";
  return b + " o";
};

function ShareModal({ doc, onClose }) {
  const [investors, setInvestors] = useState([]);
  const [shares, setShares] = useState(doc.shares || []);
  const [sel, setSel] = useState("");
  const [level, setLevel] = useState("lecture");

  useEffect(() => {
    api.get("/auth/users").then((r) => setInvestors(r.data.filter((u) => u.role === "investisseur")));
  }, []);

  const add = async () => {
    if (!sel) return;
    try { const { data } = await api.post(`/documents/${doc.id}/acl`, { user_id: sel, level }); setShares(data.shares); setSel(""); toast.success("Partage mis à jour"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const removeShare = async (uid) => {
    try { const { data } = await api.delete(`/documents/${doc.id}/acl/${uid}`); setShares(data.shares); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const sharedIds = new Set(shares.map((s) => s.user_id));
  const available = investors.filter((u) => !sharedIds.has(u.id));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="share-modal" onClick={onClose}>
      <div className="bg-white w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-head font-bold text-base flex items-center gap-2"><ShareNetwork size={18} className="text-primary" /> Partager le document</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X size={18} /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-4 truncate">{doc.name}</p>

        <div className="space-y-2 mb-4">
          {shares.length === 0 && <div className="text-sm text-muted-foreground">Aucun investisseur n'a accès.</div>}
          {shares.map((s) => (
            <div key={s.user_id} className="flex items-center gap-2 text-sm border border-border px-3 py-2" data-testid={`share-${s.user_id}`}>
              <div className="flex-1 min-w-0"><div className="font-medium truncate">{s.name}</div><div className="text-xs text-muted-foreground">{LEVEL_LABEL[s.level]}</div></div>
              <button onClick={() => removeShare(s.user_id)} className="text-muted-foreground hover:text-destructive"><X size={15} /></button>
            </div>
          ))}
        </div>

        <div className="border-t border-border pt-4">
          <label className="overline text-muted-foreground">Ajouter un investisseur</label>
          <select data-testid="share-user-select" value={sel} onChange={(e) => setSel(e.target.value)}
            className="w-full mt-1 mb-2 px-3 py-2 text-sm border border-border bg-white outline-none focus:border-primary">
            <option value="">Sélectionner…</option>
            {available.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
          <select data-testid="share-level-select" value={level} onChange={(e) => setLevel(e.target.value)}
            className="w-full mb-3 px-3 py-2 text-sm border border-border bg-white outline-none focus:border-primary">
            {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
          <button data-testid="share-add-btn" onClick={add} disabled={!sel}
            className="w-full py-2 bg-primary text-white text-sm font-semibold hover:bg-blue-800 disabled:opacity-50">Partager</button>
        </div>
      </div>
    </div>
  );
}

export default function DataRoom() {
  const { user } = useAuth();
  const isTeam = TEAM_ROLES.includes(user?.role);
  const [docs, setDocs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: "", category: "NDA", company_id: "" });
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [shareDoc, setShareDoc] = useState(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); if (isTeam) api.get("/companies").then((r) => setCompanies(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Sélectionnez un fichier à téléverser."); return; }
    setUploading(true);
    const company = companies.find((c) => c.id === form.company_id);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", form.name || file.name);
    fd.append("category", form.category);
    fd.append("company_id", form.company_id || "");
    fd.append("company_nom", company?.nom || "");
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document téléversé dans la Data Room");
      setForm({ name: "", category: "NDA", company_id: "" });
      setFile(null);
      load();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Erreur d'upload"); }
    finally { setUploading(false); }
  };

  const addVersion = async (doc, f) => {
    if (!f) return;
    setBusyId(doc.id);
    const fd = new FormData();
    fd.append("file", f);
    fd.append("document_id", doc.id);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Nouvelle version ajoutée à « ${doc.name} »`);
      load();
    } catch { toast.error("Erreur ajout de version"); } finally { setBusyId(""); }
  };

  const download = async (doc) => {
    setBusyId(doc.id);
    try { await downloadFile(`/documents/${doc.id}/download`, doc.current_filename || doc.name); }
    catch { toast.error("Erreur de téléchargement"); } finally { setBusyId(""); }
  };

  const remove = async (id) => { await api.delete(`/documents/${id}`); load(); };

  return (
    <div>
      <header className="h-16 px-8 flex items-center justify-between border-b border-border bg-white sticky top-0 z-10">
        <h1 className="font-head font-bold text-xl flex items-center gap-2"><FolderLock size={20} className="text-primary" /> Data Room</h1>
        <span className="overline text-muted-foreground flex items-center gap-1"><Lock size={12} /> Accès sécurisé · {docs.length} documents</span>
      </header>
      <div className={`p-8 grid gap-6 ${isTeam ? "lg:grid-cols-3" : "grid-cols-1"}`}>
        {isTeam && (
          <form onSubmit={submit} className="bg-white border border-border p-5 h-fit space-y-3">
            <div className="overline text-muted-foreground">Téléverser un document</div>
            <label data-testid="doc-file-label" className="flex items-center justify-center gap-2 border-2 border-dashed border-border py-5 cursor-pointer hover:bg-secondary text-sm text-muted-foreground font-medium">
              <UploadSimple size={18} /> {file ? file.name : "Choisir un fichier"}
              <input data-testid="doc-file-input" type="file" accept=".pdf,.txt,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.csv"
                onChange={(e) => { setFile(e.target.files?.[0] || null); }} className="hidden" />
            </label>
            <input data-testid="doc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du document (optionnel)"
              className="w-full px-3 py-2.5 text-sm border border-border outline-none focus:border-primary" />
            <select data-testid="doc-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select data-testid="doc-company" value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}
              className="w-full px-3 py-2.5 text-sm border border-border bg-white outline-none focus:border-primary">
              <option value="">Cabinet associé…</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
            </select>
            <button data-testid="doc-upload-btn" type="submit" disabled={uploading}
              className="w-full py-2.5 bg-primary text-white text-sm font-semibold hover:bg-blue-800 flex items-center justify-center gap-2 disabled:opacity-50">
              <FilePlus size={16} /> {uploading ? "Téléversement…" : "Téléverser"}
            </button>
          </form>
        )}

        <div className={`${isTeam ? "lg:col-span-2" : ""} bg-white border border-border`}>
          {!isTeam && (
            <div className="px-4 py-3 border-b border-border text-xs text-muted-foreground bg-slate-50" data-testid="investor-notice">
              Vous avez accès aux documents partagés avec vous par l'équipe.
            </div>
          )}
          <div className="grid grid-cols-12 px-4 py-2.5 border-b border-border overline text-muted-foreground">
            <div className="col-span-4">Document</div><div className="col-span-2">Catégorie</div>
            <div className="col-span-2">Cabinet</div><div className="col-span-1">Vers.</div><div className="col-span-3 text-right">Actions</div>
          </div>
          {docs.length === 0 && <div className="px-4 py-10 text-center text-sm text-muted-foreground">{isTeam ? "Data Room vide. Téléversez votre premier document." : "Aucun document ne vous a été partagé pour le moment."}</div>}
          {docs.map((d, i) => (
            <div key={d.id} className={`grid grid-cols-12 px-4 py-3 items-center text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`} data-testid="doc-row">
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate">{d.name}</div>
                {d.has_file && <div className="text-xs text-muted-foreground truncate">{d.current_filename} · {fmtSize(d.size)}</div>}
              </div>
              <div className="col-span-2"><span className="text-[11px] border border-border px-1.5 py-0.5">{d.category}</span></div>
              <div className="col-span-2 text-muted-foreground truncate">{d.company_nom || "—"}</div>
              <div className="col-span-1 font-mono text-xs flex items-center gap-1">
                <ClockCounterClockwise size={13} className="text-muted-foreground" /> v{d.version_count || 1}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-2">
                {d.has_file && d.can_download && (
                  <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} disabled={busyId === d.id}
                    title="Télécharger la dernière version" className="text-primary hover:text-blue-800 disabled:opacity-50"><DownloadSimple size={17} /></button>
                )}
                {d.has_file && !d.can_download && <span className="text-[11px] text-muted-foreground italic">lecture seule</span>}
                {!d.has_file && <span className="text-xs text-muted-foreground italic">référence seule</span>}
                {d.can_manage && (
                  <>
                    <label data-testid={`doc-addversion-${d.id}`} title="Ajouter une version" className="text-muted-foreground hover:text-primary cursor-pointer">
                      <UploadSimple size={17} />
                      <input type="file" className="hidden" onChange={(e) => { addVersion(d, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                    <button data-testid={`doc-share-${d.id}`} onClick={() => setShareDoc(d)} title="Partager avec un investisseur" className="text-muted-foreground hover:text-primary"><ShareNetwork size={16} /></button>
                    <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} className="text-muted-foreground hover:text-destructive"><Trash size={15} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {shareDoc && <ShareModal doc={shareDoc} onClose={() => { setShareDoc(null); load(); }} />}
    </div>
  );
}
